import { Button } from '@/components/common/Button';
import Divider from '@/components/common/Divider';
import Modal, { ModalHeader, ModalProps, ModalTitle } from '@/components/modal/Modal';
import { portalAPI, portalURL } from '@/helpers/environmentHelper';
import { ModalOperations } from '@/model/common';
import { logToServer } from '@/services/LogService';
import { ErrorMsg } from '@/types/common';
import useAbortController from '@/utils/hooks/useAbortController';
import useLanguage from '@/utils/hooks/useLanguage';
import { use401Error } from '@/utils/hooks/usePortalAuthentication';
import useToast from '@/utils/hooks/useToast';
import { useSso } from '@/utils/providers/SSOProvider';
import axios from 'axios';
import FormData from 'form-data';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FileWithPath } from 'react-dropzone-esm';
import { MdWarning } from 'react-icons/md';

export interface SkipWalletValidationModalProps extends ModalProps {
    payload: any;
    mode: ModalOperations;
    onClose?: () => void;
    setError?: Dispatch<SetStateAction<ErrorMsg>>;
    files: FileWithPath[];
}

export const SkipWalletValidationModal = (props: SkipWalletValidationModalProps) => {
    const { opened, payload, onClose, setError, files, handlers } = props;

    const { jwt } = useSso();
    const lang = useLanguage();
    const { on401Error } = use401Error();
    const [toastError, toastSuccess] = useToast();

    const [isLoading, setIsLoading] = useState(false);
    const {
        abortController,
        setAbortController,
        onClose: onAbort
    } = useAbortController({
        isLoading,
        handlers
    });

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const createWhiteListRequest = await axios.post(
                portalAPI('/api/whitelist/wallet/create'),
                {
                    ...payload,
                    skipWalletAddressValidation: 'true'
                },
                { headers: { Authorization: `Bearer ${jwt}` }, signal: abortController.signal }
            );

            if (createWhiteListRequest) {
                if (files && files.length > 0) {
                    const formData = new FormData();
                    formData.append('file', files[0]);
                    formData.append('entityId', createWhiteListRequest.data.entityId.toString());

                    if (portalURL) {
                        await axios.post(
                            portalAPI(`/api/requests/${createWhiteListRequest.data.id}/file-proof`),
                            formData,
                            {
                                headers: { Authorization: `Bearer ${jwt}` }
                            }
                        );
                    }
                }
            }

            const title = 'Whitelist Request';
            const body = `Whitelist request submitted successfully.`;
            toastSuccess({ body, title });
            onClose?.();
            handlers.close();
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || '';
            const title = 'Whitelist Request';
            const body = `An error occurred while submitting the whitelist request. ${errMsg}`;
            toastError({ body, title });
            on401Error(error, () => {
                logToServer(
                    'error',
                    `Unauthenticated access on skip wallet validation modal. JWT may be invalid or expired.`
                );
            });
            setError?.(errMsg);
            handlers.close();
        }
        setIsLoading(false);
    };

    useEffect(() => {
        if (opened) setAbortController(new AbortController());
        else abortController.abort();

        return () => abortController.abort();
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size="max-w-lg">
            <ModalHeader>
                <ModalTitle>
                    <div className="flex items-center gap-2">
                        <MdWarning className="w-6 h-6 text-yellow-500" />
                        {lang.portal.invalidWalletAddressFormat}
                    </div>
                </ModalTitle>
                {/* <ModalClose handlers={handlers} /> */}
            </ModalHeader>
            <Divider />

            <div className="p-2 sm:p-4 text-brand-text w-full overflow-auto">
                <div className="flex flex-col gap-3 text-brand-text">
                    <span>{lang.portal.theFollowingWalletAddressRequested}</span>
                    <span className="font-bold">{payload.walletAddress}</span>
                    <span className="">{lang.portal.doYouStillWishToSubmit}</span>
                </div>
            </div>
            <Divider />
            <div className="flex justify-end w-full gap-3 text-brand-text text-sm p-2 sm:p-4 flex-wrap">
                <button
                    type="button"
                    className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                    onClick={onAbort}>
                    {lang.common.cancel}
                </button>
                <Button
                    type="button"
                    className="w-auto rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                    onClick={() => {
                        if (!isLoading) handleSubmit();
                    }}
                    isLoading={isLoading}>
                    {lang.portal.submitRequest}
                </Button>
            </div>
        </Modal>
    );
};
